/**
 * US federal holidays, computed rather than stored.
 *
 * No table to maintain and no API to depend on — the rules are fixed in
 * 5 U.S.C. § 6103 and haven't changed since Juneteenth was added in 2021.
 */
function pad(n) {
    return String(n).padStart(2, '0');
}
function key(year, month, day) {
    return `${year}-${pad(month)}-${pad(day)}`;
}
/** The nth occurrence of a weekday in a month. `nth` of -1 means the last. */
function nthWeekday(year, month, weekday, nth) {
    if (nth === -1) {
        const last = new Date(year, month, 0);
        const shift = (last.getDay() - weekday + 7) % 7;
        return key(year, month, last.getDate() - shift);
    }
    const first = new Date(year, month - 1, 1);
    const offset = (weekday - first.getDay() + 7) % 7;
    return key(year, month, 1 + offset + (nth - 1) * 7);
}
/**
 * When a fixed-date holiday lands on a weekend, the federal observance moves:
 * Saturday shifts back to Friday, Sunday forward to Monday. This is the date
 * schools and offices actually close, which is usually what matters.
 */
function observedFor(dateKey) {
    const [y, m, d] = dateKey.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    const dow = date.getDay();
    if (dow === 6)
        date.setDate(date.getDate() - 1);
    else if (dow === 0)
        date.setDate(date.getDate() + 1);
    else
        return dateKey;
    return key(date.getFullYear(), date.getMonth() + 1, date.getDate());
}
/** The eleven US federal holidays for a given year. */
export function federalHolidays(year) {
    const fixed = (month, day, name) => {
        const date = key(year, month, day);
        return { date, observed: observedFor(date), name };
    };
    // Floating holidays always land on a weekday, so observed === date.
    const floating = (date, name) => ({ date, observed: date, name });
    const holidays = [
        fixed(1, 1, "New Year's Day"),
        floating(nthWeekday(year, 1, 1, 3), 'Martin Luther King Jr. Day'),
        floating(nthWeekday(year, 2, 1, 3), "Presidents' Day"),
        floating(nthWeekday(year, 5, 1, -1), 'Memorial Day'),
        fixed(6, 19, 'Juneteenth'),
        fixed(7, 4, 'Independence Day'),
        floating(nthWeekday(year, 9, 1, 1), 'Labor Day'),
        floating(nthWeekday(year, 10, 1, 2), 'Columbus Day'),
        fixed(11, 11, 'Veterans Day'),
        floating(nthWeekday(year, 11, 4, 4), 'Thanksgiving'),
        fixed(12, 25, 'Christmas Day'),
    ];
    return holidays.sort((a, b) => (a.date < b.date ? -1 : 1));
}
/**
 * Every holiday marker falling inside a date range.
 *
 * A holiday that lands on a weekend produces two markers: one on the day
 * itself and one on the observed weekday, because for a school calendar the
 * observed day is the one you're actually off.
 */
export function holidaysInRange(from, to) {
    const startYear = Number(from.slice(0, 4));
    const endYear = Number(to.slice(0, 4));
    const marks = [];
    for (let y = startYear - 1; y <= endYear + 1; y++) {
        for (const h of federalHolidays(y)) {
            if (h.date >= from && h.date <= to) {
                marks.push({ date: h.date, name: h.name, isObserved: false });
            }
            if (h.observed !== h.date && h.observed >= from && h.observed <= to) {
                marks.push({ date: h.observed, name: `${h.name} (observed)`, isObserved: true });
            }
        }
    }
    return marks.sort((a, b) => (a.date < b.date ? -1 : 1));
}
//# sourceMappingURL=holidays.js.map